/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FacebookLogin, FacebookLoginResponse } from '@capacitor-community/facebook-login';
import { FacebookUser } from '../interfaces/facebook-user-interface';

export type FacebookLoginType = 'classic' | 'limited';

export interface FacebookLoginResult {
  token: string;
  type: FacebookLoginType;
}

@Injectable({
  providedIn: 'root',
})
export class FacebookService {
  async login(): Promise<FacebookLoginResult> {
    const isIos = Capacitor.getPlatform() === 'ios';

    const result: FacebookLoginResponse = await FacebookLogin.login({
      permissions: ['email', 'public_profile'],
      ...(isIos ? { tracking: 'limited' as const } : {}),
    });

    if (!result.accessToken?.token) {
      throw new Error('Facebook login failed or was cancelled');
    }

    return {
      token: result.accessToken.token,
      type: isIos ? 'limited' : 'classic',
    };
  }

  async getUserData(token: string, type: FacebookLoginType): Promise<FacebookUser> {
    if (type === 'limited') {
      return this.decodeUserFromJWT(token);
    }

    const result = await FacebookLogin.getProfile<Record<string, unknown>>({
      fields: [
        'id',
        'name',
        'email',
        'birthday',
        'first_name',
        'last_name',
        'picture.width(800).height(800)',
      ]
    });
    return result as unknown as FacebookUser;
  }

  async logout(): Promise<void> {
    await FacebookLogin.logout();
  }

  private decodeUserFromJWT(jwt: string): FacebookUser {
    const payload = this.decodeJWT(jwt);
    return {
      id: (payload['sub'] as string) ?? '',
      name: (payload['name'] as string) ?? '',
      email: (payload['email'] as string) ?? '',
      first_name: (payload['given_name'] as string) ?? '',
      last_name: (payload['family_name'] as string) ?? '',
      birthday: (payload['birthday'] as string) ?? '',
      picture: {
        data: {
          url: (payload['picture'] as string) ?? '',
          width: 800,
          height: 800,
          is_silhouette: false,
        },
      },
    };
  }

  private decodeJWT(token: string): Record<string, any> {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token');
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  }
}
